import { useSettingsStore } from '~/stores/settings'
import { pageUrl, blogCommentsUrl, blogCommentUrl, blogSubscribeUrl, blogPostLikeUrl, cookiesUrl } from '~/helpers/urls'

const settings = useSettingsStore()

export const useBlogStore = defineStore('blog', {
  state: () => ({
    id: null,
    title: '',
    tags: [],
    subtitle: '',
    year: '',
    meta: {
      title: '',
      description: ''
    },
    og: {},
    isSubsLetterSend: false,
    isSubsLetterNotSend: false,
    isSubsLetterNotSendReason: '',
    promo_image: {},
    footer_data: {},
    content: [],
    like: {
      isLiked: false,
      id: null
    },
    likes_count: 0,
    cookie_status: {
      enabled: true,
      message: '',
      accepted: false
    },
    comments: [],
    comments_count: 0,
    isCommentSent: false,
    reply_comment: {
      isReplyCommentSent: false,
      commentId: null
    },
    comment_data: {
      followup: false,
      content_type: "blog.postpage",
      object_pk: null
    }
  }),
  actions: {
    async setSlugPage(slug) {
      if (settings?.lang_id) {
        const params0 = {
          type: 'blog.PostPage',
          fields: '*',
          descendant_of: settings.lang_id,
          slug,
        }
        const params1 = {
          type: 'blog.BlogPage',
          fields: '*',
          descendant_of: settings.lang_id,
        }
        try {
          const [res0, res1] = await Promise.all([
            useApiFetch(pageUrl, { params: params0 }),
            useApiFetch(pageUrl, { params: params1 })
          ])
          const data0 = res0.items[0]
          const data1 = res1.items[0]
          this.setSlugData(data0, data1)
          const res = await useApiFetch(`${blogCommentsUrl}${data0.id}/`)
          this.comments = res.results
        } catch (err) {
          return err.data
        }
      }
    },
    setSlugData(data0, data1) {
      this.id = data0.id
      this.title = data0.title
      this.year = data0.year
      this.likes_count = data0.likes_count
      this.comments_count = data0.comments_count
      this.promo_image = data0.promo_image
      this.footer_data = data1.content[data1.content.length - 1].value
      this.content = data0.content
      this.comment_data.object_pk = data0.id
      this.meta.description = data0.meta.search_description
      this.meta.title = data0.meta.seo_title || data0.title
      this.og = data0.og
    },
    async setIndexPage(tag) {
      const params0 = {
        type: 'blog.BlogPage',
        fields: '*',
        descendant_of: settings.lang_id,
      }
      const params1 = {
        type: 'blog.PostPage',
        fields: 'title,year,title_extra,promo_image,likes_count,comments_count,tags',
        descendant_of: settings.lang_id,
      }
      if (tag) {
        params1.tags = tag
      }
      if (settings?.lang_id) {
        try {
          const [res0, res1] = await Promise.all([
            useApiFetch(pageUrl, { params: params0 }),
            useApiFetch(pageUrl, { params: params1 })
          ])
          const data0 = res0.items[0]
          this.setIndexData(data0, res1)
        } catch (err) {
          return err.data
        }
      }
    },
    setIndexData(data0, res1) {
      this.title = data0.title
      this.tags = data0.tags
      this.subtitle = data0.title_extra
      this.footer_data = data0.content[data0.content.length - 1].value
      this.meta = {
        title: data0.meta.seo_title || data0.title,
        description: data0.meta.search_description,
      }
      this.og = data0.og
      this.content = res1.items
    },
    async sendSubsEmail(email) {
      try {
        await useApiFetch(blogSubscribeUrl, {
          method: 'POST',
          body: {
            email: email,
            active: 1
          },
        })
        this.setIsSubsLetterSend(true)
      } catch (err) {
        if (err.data.email[0] === 'Provided email exists already') {
          this.setIsSubsLetterNotSend({ notSend: true, reason: 'Provided email exists already' })
        } else {
          this.setIsSubsLetterNotSend({ notSend: true, reason: '' })
        }
      }
    },
    async getUserLike() {
      if (this?.id) {
        const params = {
          post_page: this.id,
        }
        try {
          const res = await useApiFetch(blogPostLikeUrl, { params })

          this.setUserLikeData({
            isLiked: res.count ? true : false,
            id: res.count ? res.results[0].id : null
          })
        }
        catch (err) {
          return err.data
        }
      }
    },
    async postComment(comment) {
      this.setIsCommentSent(false)
      this.setIsReplyCommentSent({ isReplyCommentSent: false, commentId: null })
      try {
        await useApiFetch(blogCommentUrl, {
          method: 'POST',
          body: { ...comment, ...this.comment_data },
          headers: {
            'Authorization': 'Token 08d9fd42468aebbb8087b604b526ff0821ce4525',
            'Content-Type': 'application/json'
          }
        })

        if (comment.reply_to !== 0) {
          this.setIsReplyCommentSent({ isReplyCommentSent: true, commentId: comment.reply_to })
        } else {
          this.setIsCommentSent(true)
        }
      } catch (err) {
        return err.data
      }
    },
    async postLike() {
      if (!this.like.isLiked) {
        try {
          const res = await useApiFetch(blogPostLikeUrl, {
            method: 'POST',
            body: {
              post_page: this.id,
            },
          })
          this.setUserLikeData({
            isLiked: true,
            id: res.id
          })
          this.addLikesCount(1)
        } catch (err) { return err.data }
      } else {
        try {
          await useApiFetch(
            `${blogPostLikeUrl}${this.like.id}/`,
            {
              method: 'DELETE',
            })
          this.setUserLikeData({
            isLiked: false,
            id: null
          })
          this.addLikesCount(-1)
        }
        catch (err) {
          return err.data
        }
      }
    },
    async checkCookies() {
      await useApiFetch(cookiesUrl).catch((err) => console.log(err.data))

      try {
        const res = await useApiFetch(cookiesUrl, {
          method: 'POST',
          body: {},
        })

        if (res?.message.toLowerCase() === 'ok') {
          this.setCookieStatus({ enabled: true, message: 'Мы используем ваши данные cookie' })
        } else {
          this.setCookieStatus({ enabled: false, message: res?.message })
        }
      } catch (err) {
        return err.data
      }
    },
    setIsSubsLetterSend(payload) {
      this.isSubsLetterSend = payload ? true : false
    },
    setIsSubsLetterNotSend(payload) {
      this.isSubsLetterNotSend = payload.notSend
      this.isSubsLetterNotSendReason = payload.reason
    },
    setUserLikeData(payload) {
      this.like.isLiked = payload.isLiked
      this.like.id = payload.id
    },
    setIsCommentSent(payload) {
      this.isCommentSent = payload ? true : false
    },
    setIsReplyCommentSent(payload) {
      this.reply_comment.isReplyCommentSent = payload.isReplyCommentSent
      this.reply_comment.commentId = payload.commentId
    },
    addLikesCount(payload) {
      this.likes_count += payload
    },
    setCookieStatus(payload) {
      this.cookie_status.enabled = payload.enabled
      this.cookie_status.message = payload.message
      this.cookie_status.accepted = payload.accepted ? true : false
    },
    resetState() {
      this.id = null
      this.title = ''
      this.year = ''
      this.likes_count = 0
      this.comments_count = 0
      this.content = []

      this.comment_data.object_pk = null
      this.meta.description = ''
      this.meta.title = ''
      this.og = {}
    }
  }
})

